import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BellRing,
  BookOpen,
  ChevronRight,
  Heart,
  LogOut,
  Moon,
  ShieldCheck,
  Sun,
} from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { authService } from "@/core/services/authService";
import { FormularioMinhasInformacoes } from "@/core/components/FormularioMinhasInformacoes";
import { useTheme } from "@/shared/theme/ThemeProvider";
import { useResumoBiblioteca } from "@/modules/biblioteca/hooks/useResumoBiblioteca";
import { CORES_STATUS, formatarData } from "@/modules/biblioteca/utils/emprestimos";
import { STATUS_LABEL } from "@/modules/biblioteca/services/emprestimosService";

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function iniciais(nome: string | undefined): string {
  const partes = (nome ?? "").trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return "?";
  const primeira = partes[0]?.[0] ?? "";
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? "") : "";
  return (primeira + ultima).toUpperCase();
}

/** "mar/2024" a partir do carimbo de criação do cadastro da pessoa no Elos. */
function mesAno(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${MESES[d.getMonth()]}/${d.getFullYear()}`;
}

/** Visual por posição no ranking de categorias favoritas (1º mais forte, 3º mais suave). */
const ESTILO_POSICAO = [
  { selo: "bg-primary text-on-primary", barra: "bg-primary" },
  { selo: "bg-primary/15 text-primary", barra: "bg-primary/70" },
  { selo: "bg-primary/10 text-primary", barra: "bg-primary/45" },
] as const;
/** "Sem categoria" não é um gosto da pessoa: aparece, mas em tom neutro. */
const ESTILO_NEUTRO = { selo: "bg-surface-2 text-muted", barra: "bg-muted/40" } as const;

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-5">
      <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted">{titulo}</h2>
      {children}
    </section>
  );
}

const LINHA_CONTA =
  "flex w-full items-center gap-3 px-1 py-3 text-left text-text transition-colors hover:bg-surface-2";

/**
 * Meu perfil — tela própria da pessoa (independente de qualquer elo).
 * Quatro blocos, só o necessário: cabeçalho, Biblioteca (resumo de leitura),
 * Minhas informações (telefone e nascimento editáveis) e Conta (notificações, segurança,
 * tema e sair). O que já existe na Início ou na Central Elos (escalas,
 * convites, elos) fica de fora de propósito.
 */
export function MeuPerfil() {
  const { pessoa, ehAdminPlataforma, temAcessoAdministrativo, modulosVisiveis, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const veBiblioteca = modulosVisiveis.includes("biblioteca");
  const biblioteca = useResumoBiblioteca(veBiblioteca ? pessoa?.id : undefined);

  // "Segurança da conta" (verificação em duas etapas) é só do Administrador
  // Fantasma por enquanto — para as demais pessoas a linha nem aparece.
  const ehFantasma = pessoa?.eh_admin_fantasma === true;

  // Selo "Proteger minha conta": só aparece quando já sabemos que não há
  // verificação em duas etapas confirmada. Falha na consulta = sem selo.
  const { data: fatoresMfa } = useQuery({
    queryKey: ["conta", "mfa", "fatores"],
    queryFn: () => authService.listarFatoresMfa(),
    retry: false,
    enabled: ehFantasma,
  });
  const precisaProteger = !!fatoresMfa && !fatoresMfa.some((f) => f.status === "verified");

  const desde = mesAno(pessoa?.created_at);
  const destinoBiblioteca = temAcessoAdministrativo ? "/admin/visualizar-como-usuario" : "/usuario";
  const ultimo = biblioteca.ultimoEmprestimo;
  const semPedidos = !biblioteca.carregando && biblioteca.totalPedidos === 0;

  async function handleSair() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
      <header className="mb-6">
        <h1 className="text-2xl font-black tracking-tight text-primary sm:text-3xl">Meu perfil</h1>
        <p className="mt-1 text-sm text-muted">Você, a sua Biblioteca e a sua conta.</p>
      </header>

      <div className="flex flex-col gap-4">
        {/* Cabeçalho */}
        <section className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5">
          <span
            aria-hidden="true"
            className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-primary text-xl font-black text-on-primary"
          >
            {iniciais(pessoa?.nome_completo)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-bold text-text">{pessoa?.nome_completo}</p>
            <p className="truncate text-sm text-muted">{pessoa?.email}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                {ehAdminPlataforma ? "Administrador da igreja" : "Membro"}
              </span>
              {desde && <span className="text-xs text-muted">No Elos desde {desde}</span>}
            </div>
          </div>
        </section>

        {/* Biblioteca */}
        {veBiblioteca && (
          <Secao titulo="Biblioteca">
            {semPedidos ? (
              <div className="py-2 text-center">
                <p className="text-sm text-muted">Você ainda não pediu nenhum livro.</p>
                <button
                  type="button"
                  onClick={() => navigate(destinoBiblioteca, { state: { abaInicial: "catalogo" } })}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline"
                >
                  <BookOpen size={16} />
                  Ver o catálogo
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { n: biblioteca.devolvidos, um: "livro devolvido", varios: "livros devolvidos" },
                    { n: biblioteca.emAndamento, um: "em andamento", varios: "em andamento" },
                    { n: biblioteca.atrasados, um: "atrasado", varios: "atrasados" },
                  ].map((item) => (
                    <div key={item.um} className="rounded-xl bg-surface-2 p-3">
                      <p className="text-2xl font-black text-primary">{item.n}</p>
                      <p className="mt-1 text-xs text-muted">{item.n === 1 ? item.um : item.varios}</p>
                    </div>
                  ))}
                </div>

                {ultimo && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-muted">Último empréstimo</p>
                    <div className="mt-1.5 flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-text">{ultimo.livro_titulo}</p>
                        <p className="text-xs text-muted">{formatarData(ultimo.data_solicitacao)}</p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${CORES_STATUS[ultimo.status]}`}
                      >
                        {STATUS_LABEL[ultimo.status]}
                      </span>
                    </div>
                  </div>
                )}

                {biblioteca.categoriasFavoritas.length > 0 && (
                  <div className="mt-5 border-t border-border pt-4">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-muted">
                      <Heart size={14} className="text-primary" aria-hidden="true" />
                      Categorias favoritas
                    </p>
                    <ol className="mt-3 flex flex-col gap-3.5">
                      {biblioteca.categoriasFavoritas.map(([categoria, contagem], indice) => {
                        const maior = biblioteca.categoriasFavoritas[0]?.[1] ?? 1;
                        const estilo =
                          categoria === "Sem categoria"
                            ? ESTILO_NEUTRO
                            : (ESTILO_POSICAO[indice] ?? ESTILO_POSICAO[2]);
                        return (
                          <li key={categoria} className="flex items-center gap-3">
                            <span
                              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-black ${estilo.selo}`}
                              aria-hidden="true"
                            >
                              {indice + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-3">
                                <span
                                  className={`min-w-0 truncate text-sm text-text ${indice === 0 ? "font-bold" : "font-semibold"}`}
                                >
                                  {categoria}
                                </span>
                                <span className="shrink-0 text-xs text-muted">
                                  <span className="font-bold text-text">{contagem}</span>{" "}
                                  {contagem === 1 ? "empréstimo" : "empréstimos"}
                                </span>
                              </div>
                              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface-2">
                                <div
                                  className={`h-full rounded-full ${estilo.barra}`}
                                  style={{ width: `${Math.max(8, Math.round((contagem / maior) * 100))}%` }}
                                />
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => navigate(destinoBiblioteca, { state: { abaInicial: "meus" } })}
                  className="mt-4 w-full rounded-xl border border-border py-2.5 text-sm font-bold text-primary transition-colors hover:bg-surface-2"
                >
                  Ver meus empréstimos
                </button>
              </>
            )}
          </Secao>
        )}

        {/* Minhas informações */}
        <Secao titulo="Minhas informações">
          {pessoa && <FormularioMinhasInformacoes pessoa={pessoa} />}
        </Secao>

        {/* Conta */}
        <Secao titulo="Conta">
          <div className="-my-1 divide-y divide-border">
            <button type="button" onClick={() => navigate("/conta/notificacoes")} className={LINHA_CONTA}>
              <BellRing size={20} className="shrink-0 text-muted" />
              <span className="flex-1 text-sm font-semibold">Notificações</span>
              <ChevronRight size={16} className="shrink-0 text-muted" />
            </button>
            {ehFantasma && (
              <button type="button" onClick={() => navigate("/conta/seguranca")} className={LINHA_CONTA}>
                <ShieldCheck size={20} className="shrink-0 text-muted" />
                <span className="flex-1 text-sm font-semibold">Segurança da conta</span>
                {precisaProteger && (
                  <span className="shrink-0 rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-semibold text-warning">
                    Proteger minha conta
                  </span>
                )}
                <ChevronRight size={16} className="shrink-0 text-muted" />
              </button>
            )}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Alternar tema claro/escuro"
              className={LINHA_CONTA}
            >
              {theme === "dark" ? (
                <Moon size={20} className="shrink-0 text-muted" />
              ) : (
                <Sun size={20} className="shrink-0 text-muted" />
              )}
              <span className="flex-1 text-sm font-semibold">
                {theme === "dark" ? "Tema escuro" : "Tema claro"}
              </span>
              <span
                aria-hidden="true"
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                  theme === "dark" ? "bg-primary" : "bg-border"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-transform ${
                    theme === "dark" ? "left-0.5 translate-x-5" : "left-0.5"
                  }`}
                />
              </span>
            </button>
            <button type="button" onClick={handleSair} className={LINHA_CONTA}>
              <LogOut size={20} className="shrink-0 text-muted" />
              <span className="flex-1 text-sm font-semibold">Sair</span>
            </button>
          </div>
        </Secao>
      </div>
    </main>
  );
}
